export default (
  { req, res, route, app, store, $axios, $cookies, redirect, env, $clerk },
  inject
) => {

  // Serialised save queue — prevents concurrent /cart/calculate calls from
  // racing and overwriting each other's results in localStorage / Vuex.
  let _saveCartQueue = Promise.resolve();
  // Coalesce rapid-fire saves: if a save is already queued behind the
  // in-flight one, we don't need a third — the queued one will re-read Vuex
  // and send the latest state anyway.
  let _pendingSaveSkip = null; // null = no pending; otherwise the skip flag for the queued save
  // Tombstones for items the user explicitly removed. Filtered out of the
  // post-save merge so that an in-flight add-save's response can't resurrect
  // a deleted item. addToCart drops a tombstone when the same item is
  // re-added (user reversed their decision).
  const _pendingTombstones = new Set();
  const _tombstoneKey = (item) =>
    `${item.barcode}::${JSON.stringify(item.variation || [])}`;

  // Safety net for cartRecalculating: the flag gates the checkout button, so
  // it must never survive a lost callback. Armed every time it is raised.
  let _recalcWatchdog = null;
  const _setRecalculating = (on) => {
    try {
      store.commit("cartRecalculating", on);
    } catch (e) {}
    if (_recalcWatchdog) {
      clearTimeout(_recalcWatchdog);
      _recalcWatchdog = null;
    }
    if (on && process.browser) {
      _recalcWatchdog = setTimeout(() => {
        try {
          store.commit("cartRecalculating", false);
        } catch (e) {}
      }, 45000);
    }
  };

  const saveCart = async (cart, skip) => {
    // Commit the intended cart to Vuex immediately (optimistic update) so
    // that any concurrent addToCart / removeItem call reads current state.
    const _optimistic = JSON.parse(JSON.stringify(cart));
    store.commit("cart", _optimistic);
    // #17739 (fix 2): eager-persist the optimistic state so localStorage never
    // lags Vuex while a save is in flight. Without this, getCart() — which reads
    // localStorage and commits it back into Vuex — can revert a just-added item
    // during the in-flight window (the "cart empties on add" SOS bug). The
    // in-flight _executeSaveCart still re-writes localStorage with the priced
    // server-merged cart, so this only ever holds the (complete) item set.
    if (process.browser) {
      try {
        localStorage.setItem("cart", JSON.stringify(_optimistic));
      } catch (e) {}
    }
    // From here until the queue drains the displayed totals belong to an
    // older cart than the one on screen. Tell the summary to grey them out
    // rather than let them read as final (#23407).
    if (skip !== true) {
      _setRecalculating(true);
    }
    if (_pendingSaveSkip !== null) {
      // There is already a queued save waiting behind the in-flight one.
      // Tighten its skip flag (false wins, to guarantee we recalculate).
      if (skip === false) _pendingSaveSkip = false;
      return _saveCartQueue;
    }
    _pendingSaveSkip = skip;
    _saveCartQueue = _saveCartQueue
      .catch(() => {})
      .then(() => {
        const nextSkip = _pendingSaveSkip;
        _pendingSaveSkip = null;
        return _executeSaveCart(nextSkip);
      });
    return _saveCartQueue;
  };

  const _executeSaveCart = async (skip) => {
    // Declared out here so the `finally` below can see it.
    let _quantityDrift = false;
    try {
    // Snapshot Vuex at dequeue time — this is what we POST.
    let preCart = JSON.parse(JSON.stringify(store.getters.cart));
    if (preCart.then && typeof preCart.then == "function") {
      preCart = await preCart;
    }
    let cart = preCart;
    if (!skip) {
      cart = await remoteCalculateCart(preCart);
    }
    if (cart.then && typeof cart.then == "function") {
      cart = await cart;
    }
    // Items the server explicitly dropped (e.g. catalog disabled them — F4).
    // Don't merge these back from Vuex even though they're "missing" in the
    // response; the server intentionally removed them.
    const droppedKeys = new Set(((cart.dropped) || []).map(_tombstoneKey));
    // Merge: any item that exists in Vuex NOW but not in the server response
    // was added while this save was in flight. Carry it forward so the next
    // queued save sees it and the server eventually gets it.
    const currentCart = store.getters.cart || { items: [] };
    const missing = (currentCart.items || []).filter(
      (ci) =>
        !droppedKeys.has(_tombstoneKey(ci)) &&
        !(cart.items || []).find(
          (ri) =>
            ri.barcode == ci.barcode &&
            JSON.stringify(ri.variation) == JSON.stringify(ci.variation)
        )
    );
    if (missing.length) {
      cart.items = [...(cart.items || []), ...missing];
    }
    // A quantity the user changed while this save was in flight is NOT covered
    // by the `missing` merge above - the item is present in the response, just
    // carrying the quantity we POSTED. Committing that verbatim throws the
    // click away: six clicks on "+" moved a line from 3 to 4 (#23407). Compare
    // what we sent against what Vuex holds now, keep the user's number, and
    // re-save so the totals catch up to it.
    (cart.items || []).forEach((ri) => {
      const k = _tombstoneKey(ri);
      const posted = (preCart.items || []).find((pi) => _tombstoneKey(pi) == k);
      const now = (currentCart.items || []).find((ci) => _tombstoneKey(ci) == k);
      if (!posted || !now) {
        return;
      }
      if (now.quantity != posted.quantity && ri.quantity != now.quantity) {
        ri.quantity = now.quantity;
        _quantityDrift = true;
      }
    });
    // Drop the transient hint before persisting client-side state.
    delete cart.dropped;
    // Apply tombstones — drop any item the user explicitly removed since
    // this save started. Without this, the server response of an in-flight
    // add-save can resurrect a just-removed item (F1 in cart-bug-fixes plan).
    if (_pendingTombstones.size > 0) {
      cart.items = (cart.items || []).filter(
        (it) => !_pendingTombstones.has(_tombstoneKey(it))
      );
      _pendingTombstones.clear();
    }
    if (store.getters.token) {
      cart.token = store.getters.token;
    }
    localStorage.setItem("cart", JSON.stringify(cart));
    store.commit("cart", cart);
    if ($nuxt) {
      $nuxt.$emit("updateCart");
    }
    $nuxt.$cookies.set("sum", cart.items.map((i) => i.barcode).join("_"), {
      domain: `.${store.getters.store.eshop.url}`,
    });
    if (_quantityDrift) {
      // Deliberately not awaited: this chains onto _saveCartQueue behind the
      // current run. Awaiting it here would wait on a promise that cannot
      // settle until this function returns.
      saveCart(cart);
    }
    return cart;
    } finally {
      // Only the last save standing lowers the flag: a drift re-save or a
      // queued save means the figures on screen are still provisional.
      if (!_quantityDrift && _pendingSaveSkip === null) {
        _setRecalculating(false);
      }
    }
  };
  const getRules = (cart) => {
    let rules = [];
    (store.getters.store.eshop.rules || []).forEach((cr) => {
      if ((cr.results || []).length) {
        rules.push(processRule(cart, cr));
      }
    });
    return rules;
  };

  const processRule = (cart, r) => {
    let time = Date.now();
    let valids = [];
    let labels = [];
    (r.conditions || []).forEach((c) => {
      if (c.type == "giftCard") {
        if ((cart.giftcard || {}).code == c.value) {
          valids.push(true);
        } else {
          valids.push(false);
        }
      } else if (c.type == "validFrom") {
        let from = new Date(c.value).getTime();
        if (time >= from) {
          valids.push(true);
        } else {
          valids.push(false);
        }
      } else if (c.type == "validTo") {
        let to = new Date(c.value).getTime();
        if (time < to) {
          valids.push(true);
        } else {
          valids.push(false);
        }
      } else if (c.type == "totalCost") {
        if (cart.totalC + cart.shipping.cost + cart.payment.cost > c.value) {
          valids.push(true);
        } else {
          valids.push(false);
        }
      } else if (c.type == "productCost") {
        if (cart.totalC > c.value) {
          valids.push(true);
        } else {
          valids.push(false);
        }
      } else if (c.type == "items") {
        valids.push(
          !!(
            (cart.items || []).filter((i) => c.value.includes(i.barcode)) || []
          ).length
        );
      } else if (c.type == "key") {
        valids.push(
          !!((cart.items || []).filter((i) => i[c.key] == c.value) || []).length
        );
      } else if (c.type == "emails") {
        if (!store.getters.token) {
          valids.push(false);
        } else if ((c.value || "").includes((store.getters.user || {}).email)) {
          valids.push(true);
        } else {
          valids.push(false);
        }
      } else if (c.type == "totalOrders") {
      }
      labels.push(c.type);
    });
    let valid;
    if (r.comparison == "&&") {
      valid = !valids.includes(false);
    } else {
      valid = valids.includes(true);
    }
    if (!valid) {
      return;
    }
    let results = [];
    (r.results || []).forEach((re) => {
      if (re.type == "costDiscount") {
        results.push(
          `${r.title}:${r.description}:${re.friendly_title}:sum:${re.value}`
        );
      } else if (re.type == "costDiscountPercentage") {
        results.push(
          `${r.title}:${r.description}:${re.friendly_title}:sum%:${re.value}`
        );
      } else if (re.type == "productDiscount") {
        results.push(
          `${r.title}:${r.description}:${re.friendly_title}:totalC:${re.value}`
        );
      } else if (re.type == "productDiscountPercentage") {
        results.push(
          `${r.title}:${r.description}:${re.friendly_title}:totalC%:${re.value}`
        );
      } else if (re.type == "shippingDiscount") {
        results.push(
          `${r.title}:${r.description}:${re.friendly_title}:shipping.cost:${re.value}`
        );
      } else if (re.type == "shippingDiscountPercentage") {
        results.push(
          `${r.title}:${r.description}:${re.friendly_title}:shipping.cost%:${re.value}`
        );
      } else if (re.type == "freeProduct") {
        results.push(
          `${r.title}:${r.description}:${re.friendly_title}:items:${re.value}`
        );
      }
    });
    return results;
  };

  const applyRules = async (cart) => {
    cart.rules = getRules(cart);
    cart.rullings = [];
    cart.rules = cart.rules.filter((x) => x);
    await Promise.all(
      (cart.rules || []).map(async (rr, rrindex) => {
        let rule = {};
        rule.rulling = 0;
        rule.rulling_shipping = 0;
        rule.rulling_free = "";
        rule.rulling_friendly = [];
        rule.rulling_items = [];
        await Promise.all(
          rr.map(async (r) => {
            rule.title = r.split(":")[0];
            rule.description = r.split(":")[1];
            let friendly_title = r.split(":")[2];
            let key = r.split(":")[3];
            let value = r.split(":")[4];
            if (key == "sum") {
              rule.rulling += parseFloat(value);
              rule.rulling_friendly.push({
                title: friendly_title,
                value: parseFloat(value),
                calculated: (parseFloat(value) || 0).toFixed(2),
              });
            } else if (key == "sum%" && value) {
              rule.rulling += parseFloat(
                (cart.totalC + cart.shipping.cost + cart.payment.cost) *
                  (value / 100)
              );
              rule.rulling_friendly.push({
                title: friendly_title,
                value: value + "%",
                calculated: (
                  parseFloat(
                    (cart.totalC + cart.shipping.cost + cart.payment.cost) *
                      (value / 100)
                  ) || 0
                ).toFixed(2),
              });
            } else if (key == "totalC") {
              rule.rulling += parseFloat(value);
              rule.rulling_friendly.push({
                title: friendly_title,
                value: parseFloat(value),
                calculated: parseFloat(value).toFixed(2),
              });
            } else if (key == "totalC%" && value) {
              rule.rulling += parseFloat(cart.totalC * (value / 100));
              rule.rulling_friendly.push({
                title: friendly_title,
                value: value + "%",
                calculated: (
                  parseFloat(cart.totalC * (value / 100)) || 0
                ).toFixed(2),
              });
            } else if (key == "shipping.cost") {
              rule.rulling_shipping += parseFloat(value);
              rule.rulling_friendly.push({
                title: friendly_title,
                value: value,
                calculated: parseFloat(value).toFixed(2),
              });
            } else if (key == "shipping.cost%" && value) {
              rule.rulling_shipping += parseFloat(
                cart.shipping.cost * (value / 100)
              );
              rule.rulling_friendly.push({
                title: friendly_title,
                value: value + "%",
                calculated: (
                  parseFloat(cart.shipping.cost * (value / 100)) || 0
                ).toFixed(2),
              });
            } else if (key == "items") {
              rule.rulling_free = value;
              try {
                let temp = await $nuxt.$axios.$get(
                  `/items/store/${store.getters.store.id}?store_id=${store.getters.store.id}&barcodes=${rule.rulling_free}`
                );
                if (temp.items && temp.items.docs) {
                  rule.rulling_items = temp.items.docs
                    .map((i) => {
                      i.variation = [
                        {
                          hidden: false,
                          option: true,
                          key: friendly_title || "",
                          value: "ΔΩΡΟ",
                          title: "",
                          type: "price",
                          price: "0",
                        },
                      ];
                      i.quantity = 1;
                      i.ruled = rule.uid;
                      return i;
                    })
                    .filter((i) => {
                      return !(
                        cart.items.filter((ci) => ci.ruled == rule.uid) || []
                      ).length;
                    });
                }
              } catch (error) {}
            }
            return false;
          })
        );
        cart.rullings.push(rule);
        return true;
      })
    );
    return cart;
  };
  const calculateCart = async (cart) => {
    if (store.getters.store.eshop.cartCalculate == "remote") {
      return await remoteCalculateCart(cart);
    }
    if (!store.getters.token) {
      delete cart.rullings;
      cart.items = cart.items.filter((i) => !i.ruled);
    }
    cart.items = cart.items.map((i) => {
      i.quantity = i.quantity < 1 ? 1 : i.quantity;
      return i;
    });
    cart.totalQ = cart.items.reduce((total, i) => {
      return total + parseInt(i.quantity);
    }, 0);
    cart.totalC = cart.items.reduce((total, i) => {
      return total + app.$hyperItem.actualPrice(i, false, i.quantity);
    }, 0);
    cart.vat = cart.items.reduce((total, i) => {
      let ic = app.$hyperItem.actualPrice(i, false, i.quantity);
      return total + ic / (1 + (parseFloat(i.prices.vat) || 24) / 100);
    }, 0);

    cart.shippingLocks = [
      ...cart.items.map((i, index) => {
        return (i.shipping || {}).type;
      }),
    ].filter((x) => x);
    cart.paymentLocks = [
      ...cart.items.map((i, index) => {
        return (i.payment || {}).type;
      }),
    ].filter((x) => x);

    if (
      cart.shipping.options &&
      cart.shipping.options.filter((o) => o.key == "free")
    ) {
      let temp = cart.shipping.options.filter((o) => o.key == "free")[0];
      if (temp && temp.value < cart.totalC) {
        cart.shipping.memory = cart.shipping.memory || cart.shipping.cost;
        cart.shipping.cost = 0;
      } else {
        cart.shipping.cost = cart.shipping.memory || cart.shipping.cost;
      }
    }
    let giftcardCost = 0;
    if (
      cart.giftcard &&
      cart.giftcard.value &&
      cart.giftcard.type == "percentage"
    ) {
      giftcardCost = (cart.totalC * cart.giftcard.value) / 100;
    } else if (cart.giftcard && cart.giftcard.value) {
      giftcardCost = cart.giftcard.value;
    }
    (cart.giftcard || {}).cost = giftcardCost;
    cart.vat = cart.vat;
    let rulling = (cart.rullings || []).reduce((acc, r) => {
      return acc + (r.rulling || 0) + (r.rulling_shipping || 0);
    }, 0);
    cart.sum =
      (cart.totalC || 0) +
      (cart.shipping.cost || 0) +
      (cart.payment.cost || 0) -
      (giftcardCost || 0) -
      (rulling || 0);
    if (store.getters.token) {
      cart = await applyRules(cart);
    }
    return cart;
  };
  const fetchCart = async () => {
    try {
      if (!$cookies.get("cart_id")) {
        return null
      }
      let t = await $axios.$get(
        `/cart?store_id=${store.getters.store.id}&id=${$cookies.get(
          "cart_id"
        )}`,
        {
          headers: {
            authorization: `Bearer ${$cookies.get("token") || ""}`,
            from: req?.url,
          },
        }
      );
      if (t && t.cart) {
        if (
          !t.cart?.user?.id &&
          store.getters.user?.id &&
          !t.cart.operator?.id
        ) {
          t.cart.user = store.getters.user;
        }
        return t.cart;
      } else {
        // Cart not found or document is malformed. Clear the stale cookie so
        // the next /cart/calculate creates a fresh cart and sets a new one.
        // Do NOT touch localStorage — items added in this session must survive.
        $cookies.remove('cart_id', {
          domain: `.${store.getters.store.eshop.url}`,
        });
        return null;
      }
    } catch (e) {
      console.error(e);
      throw new Error(e);
    }
  };
  const remoteCalculateCart = async (cart, _retried) => {
    if (cart.id != $cookies.get("cart_id") && $cookies.get("cart_id")) {
      let remotecart = await fetchCart();
      if (remotecart) {
        // Local cart is authoritative: it reflects every add, remove, and
        // quantity change the user made in this session. We must NOT replace
        // it with the remote copy — that would lose items and revert removes.
        // Instead: adopt the remote cart's ID (so cookie and cart stay in
        // sync) and add any items that exist only on the remote side (e.g.
        // added from another tab). Local quantities/removes always win.
        const remoteOnlyItems = (remotecart.items || []).filter(
          (ri) =>
            !(cart.items || []).find(
              (li) =>
                li.barcode == ri.barcode &&
                JSON.stringify(li.variation) == JSON.stringify(ri.variation)
            )
        );
        if (remoteOnlyItems.length) {
          cart.items = [...(cart.items || []), ...remoteOnlyItems];
        }
        cart.id = remotecart.id || $cookies.get("cart_id");
      }
    }
    try {
      let r = process.browser ? $nuxt.$route.name : route.name;

      // Send the last known server updated_at so the API can enforce
      // optimistic concurrency against simultaneous writers (other tabs).
      const cartPayload = { ...cart };
      if (cart.updated_at) {
        cartPayload.client_updated_at = cart.updated_at;
      }
      // Send any pending tombstones (items the user explicitly removed
      // since the last sync) so the server can log them and veto stale
      // adds from other tabs (F2).
      if (_pendingTombstones.size > 0) {
        cartPayload.tombstones = Array.from(_pendingTombstones);
      }
      let temp = await $axios.$post(
        `cart/calculate`,
        {
          store_id: store.getters.store.id,
          cart: cartPayload,
          cart_id: cart.id || $cookies.get("cart_id"),
          route:r,
        },
        {
          headers: {
            Authorization: `Bearer ${store.getters.token}`,
          },
        }
      );
      // Always sync the cookie to the server's canonical cart ID. This covers
      // the case where a cart expired and syncCart created a new one with a
      // different ID — without this update the cookie would stay stale forever.
      const _canonicalId = (temp.cart || temp).id;
      if (_canonicalId) {
        $cookies.set("cart_id", _canonicalId, {
          domain: `.${store.getters.store.eshop.url}`,
        });
      }
      if (temp.shippings) {
        let ss = store.getters.store.shippings.map(
          (s) => {
            let t = temp.shippings.find((m) => m._id == s._id)
            if(t){
              s.cost = t.cost;
            }
            return s;
          }
        );
        store.commit('store',{key:'shippings',value:ss})
      }
      if (temp.payments) {
        let pp = store.getters.store.payments.map((s) => {
          let t = temp.payments.find((m) => m._id == s._id);
          if(t){
            s.cost = t.cost;
            s.installments = t.installments;
          }
          return s;
        });
        store.commit('store',{key:'payments',value:pp})
      }
      if (!temp.cart) {
        return temp;
      }
      //TODO - pick gift
      let picks = (temp.cart.rullings||[]).filter(r=>r.pick_from);
      let uids = picks.map(r=>r.uid);
      let selected = temp.cart.items.filter(i=>uids.includes(i.ruled));
      if(picks.length && selected.length != uids.length){
        $nuxt.$emit("pick_gift", {
          promos: picks,
          open: true,
        });
      }
      return temp.cart;
    } catch (error) {
      // Never hand back an unpriced cart as if it were priced. saveCart() has
      // already committed the new items optimistically with the PREVIOUS totals,
      // so plainly returning `cart` here is what left a 5-item cart showing a
      // 2-item payable total (#23407).
      console.error(error);
      if (!_retried) {
        // Recurse rather than re-post by hand, so the retry keeps the same
        // client_updated_at + tombstones and cannot resurrect a removed item.
        return await remoteCalculateCart(cart, true);
      }
      // Still failing: mark the cart unpriced. The template already renders
      // invalid.message and already disables every checkout button on !valid, so
      // the customer gets a warning and cannot order against an unverified total.
      cart.valid = false;
      cart.invalid = {
        ...(cart.invalid || {}),
        message:
          "Δεν μπορέσαμε να ενημερώσουμε το σύνολο του καλαθιού. Ανανεώστε τη σελίδα και δοκιμάστε ξανά.",
      };
      try {
        store.commit("cartError", "cart_recalculate_failed");
      } catch (e) {}
      return cart;
    }
  };
 
  const getCart = (calculate,fromWeb) => {
    if (fromWeb) {
      return fetchCart();
    }
    let temp = JSON.parse(localStorage.getItem("cart"));
    let default_shipping = defaultShipping(temp);
    if (!temp) {
      temp = {
        items: [],
        totalQ: 0,
        totalC: 0,
        comments: "",
        rullings: [],
        shipping: default_shipping || {
          cost: 0,
          address: {},
        },
        services: [],
        payment: {
          cost: 0,
        },
        parast: {
          type: "receipt",
        },
        user: {},
        checkout: "guest",
        step: 0,
      };
    }
    if (!(temp.shipping||{}).type) {
      temp.shipping = default_shipping;
    }
    if (!temp.shipping2) {
      temp.shipping2 = {};
    }
    if (!temp.billingAddress) {
      temp.billingAddress = false;
    }
    // #17739 (fix 1): getCart() must never clobber the in-memory cart with a
    // lagging localStorage snapshot. Carry forward any Vuex item that is missing
    // from the snapshot (an add whose save is still in flight), and drop any item
    // the user is removing right now (in-flight tombstone) so a stale snapshot
    // can't resurrect it. This makes the read non-destructive regardless of caller.
    const live = store.getters.cart || { items: [] };
    const carried = (live.items || []).filter(
      (li) =>
        !(temp.items || []).some(
          (ti) => _tombstoneKey(ti) === _tombstoneKey(li)
        )
    );
    if (carried.length) {
      temp.items = [...(temp.items || []), ...carried];
    }
    if (_pendingTombstones.size > 0) {
      temp.items = (temp.items || []).filter(
        (it) => !_pendingTombstones.has(_tombstoneKey(it))
      );
    }
    if (calculate) {
      return calculateCart(temp);
    }
    store.commit("cart", temp);
    return temp;
  };

  const addToCart = async (
    titem,
    cart,
    previousQuantity,
    noPop,
    noAlert,
    error
  ) => {
    if (!Array.isArray(titem)) {
      titem = [titem];
    }
    if (!cart) {
      cart = JSON.parse(JSON.stringify(store.getters.cart));
    }
    if (cart.then && typeof cart.then == "function") {
      cart = await cart;
    }
    for (let i = 0; i < titem.length; i++) {
      let item = JSON.parse(JSON.stringify(titem[i]));
      if (!item._id && item.id) {
        item._id = item.id;
      }
      if (!item.id && item._id) {
        item.id = item._id;
      }
      // The user is explicitly putting this item back — reject any
      // outstanding remove-tombstone for it (F1).
      _pendingTombstones.delete(_tombstoneKey(item));
      delete item.comment;
      let found = -1;
      if (!item.quantity) {
        item.quantity = 1;
      }
      if (item.stock) {
        item.fromStock = true;
      }
      Object.keys(item).forEach((k) => {
        if (k.includes("tags_")) {
          delete item[k];
        }
      });
      delete item.flat_tags;
      let required = [];
      if (item.options) {
        item.options.forEach((o) => {
          if (o.required) {
            required = [...required, o.key];
          }
        });
        if (item.variation) {
          item.variation = item.variation.filter((v) => v);
          item.variation.forEach((v) => {
            if (required.includes(v.key)) {
              required.splice(required.indexOf(v.key), 1);
            }
          });
        }
        if (required.length && !error) {
          store.commit("optionsRequired", {
            ...item,
            required,
          });
          return false;
        } else if (required.length && error) {
          return "options";
        }
      }
      item.prices.retail_price =
        parseFloat(item.prices.actual_price) || item.prices.retail_price;
      cart.items.forEach((i, index) => {
        if (i.barcode == item.barcode) {
          if (JSON.stringify(i.variation) == JSON.stringify(item.variation)) {
            found = index;
          }
        }
      });
      delete cart.skip;

      if (found < 0) {
        if (previousQuantity) {
          item.quantity = previousQuantity;
        }
        cart.items.push(item);
      } else {
        cart.items[found].quantity =
          parseFloat(cart.items[found].quantity) + parseFloat(item.quantity);
        item = cart.items[found];
        cart.items[found].prices = item.prices;
        cart.items[found].availability = item.availability;
      }
      let img = (item.images||[])[0] || "/fallback.svg";
      if (!noAlert) {
        $clerk.hyperAlert({
          time: 2000,
          title: "Προστέθηκε στο καλάθι!",
          icon: "success",
          html: `<img class="h-16 rounded-hypert" onerror="this.src='/fallback.svg'" src="${img}"/>`,
        });
      }
      item.inCart = true;
      if (process.browser) {
        try {
          window.navigator.vibrate(200);
        } catch (error) {}
      }
      store.commit("addToCartItem", item);
      $nuxt.$emit("informant:addToCart", { item });
      titem[i] = item;
    }
    // Commit to Vuex immediately so that any concurrent addToCart call that
    // fires before this save completes reads the up-to-date cart, not stale state.
    store.commit("cart", cart);
    await saveCart(cart, false);
    if (!noPop) {
      $nuxt.$emit("addToCartPop");
    }
    return (titem || [])[0];
  };

  const addFromBarcodes = async (bcs, skip, noPop, checkPromos) => {
    if (!bcs) {
      return;
    }
    let sv = ``;
    if (store.getters.store.eshop.search.includes("elastic")) {
      sv = "sv/";
    }
    let temp = await $axios.$get(
      `items/${sv}store/${store.getters.store.store_id}?store_id=${store.getters.store.store_id}&barcodes=${bcs}`,
      {
        headers: {
          Authorization: `Bearer ${store.getters.token}`,
        },
      }
    );
    if ((((temp || {}).items || {}).docs || []).length) {
      addToCart(temp.items.docs, false,1, skip, noPop);
    } else {
      $clerk.hyperAlert({
        time: 2000,
        title: "Δεν βρέθηκαν προϊόντα!",
        icon: "info",
      });
    }
    if (checkPromos && (temp.items.docs || [])[0]) {
      let item = temp.items.docs[0];
      app.$hyperItem.promos(item, "smartbuy", true);
    }
  };

  const removeItem = async (item, cart) => {
    // Mark the item as user-removed so any in-flight save's merge can't
    // resurrect it (F1). The tombstone is dropped in addToCart if the user
    // re-adds the same item, and in _executeSaveCart after a save completes.
    _pendingTombstones.add(_tombstoneKey(item));
    cart.items = cart.items.filter((i) => {
      if (i.id != item.id) {
        return i;
      } else if (i.id == item.id) {
        if (JSON.stringify(i.variation) != JSON.stringify(item.variation)) {
          return i;
        }
      }
    });
    await saveCart(cart);
    $nuxt.$emit("updateCart");
    $nuxt.$emit("informant:removeItem", { item });
    return;
  };

  const checkCartQuantity = async (item, cart) => {
    if (item.moq && item.moq > item.quantity) {
      item.quantity = item.moq;
    } else if (item.maxoq && item.maxoq < item.quantity) {
      item.quantity = item.maxoq;
    }
    await saveCart(cart);
    $nuxt.$emit("updateCart");
    return;
  };
  const increaseCartQuantity = async (item, cart, external) => {
    if (item.maxoq && item.quantity >= item.maxoq) {
      item.quantity = item.maxoq;
      return;
    }
    item.quantity = parseInt(item.quantity) + 1;
    if (external) {
      let i = cart.items.find(
        (i) =>
          i.barcode == item.barcode &&
          JSON.stringify(i.variation) == JSON.stringify(item.variation)
      );
      if (i) {
        i.quantity = item.quantity;
      }
    }
    if(item.giftsMaximum && item.giftsMaximum >= item.quantity){
      app.$hyperItem.promos(item, "smartbuy", true);
    }
    $clerk.hyperAlert({
      time: 2000,
      title: "Προστέθηκε στο καλάθι!",
      icon: "success",
      html: `<img class="h-16 rounded-hypert" onerror="this.src='/fallback.svg'" src="${item.images[0]}"/>`,
    });
    await saveCart(cart);
    $nuxt.$emit("updateCart");
    $nuxt.$emit("informant:addToCart", { item });
    return;
  };
  const decreaseCartQuantity = async (item, cart, external) => {
    if (item.moq && item.quantity <= item.moq) {
      item.quantity = item.moq;
    } else if (item.quantity == 1) {
      item.quantity = 1;
    } else {
      item.quantity = parseInt(item.quantity) - 1;
    }
    if (external) {
      let i = cart.items.find(
        (i) =>
          i.barcode == item.barcode &&
          JSON.stringify(i.variation) == JSON.stringify(item.variation)
      );
      if (i) {
        i.quantity = item.quantity;
      }
    }
    
    // giftsMaximum is only ever set by the pick-a-gift popup. For rule-granted
    // gifts it is undefined, which made the trim below mark nothing as `keep`
    // and drop every gift on the line (#23407). Those are the server's to
    // manage - saveCart() recalculates the rules and its answer wins.
    if (item.gifts && item.giftsMaximum) {
      let totalGifts = item.gifts.reduce((acc, g) => acc + g.quantity, 0);
      if(totalGifts>item.quantity){
        let pool = item.giftsMaximum;
        if (pool > item.quantity) {
          pool = item.quantity;
        }
        if (totalGifts >= item.quantity) {
          for (let j = 0; j < item.gifts.length; j++) {
            if (item.gifts[j].quantity >= pool && pool) {
              pool -= item.gifts[j].quantity;
              item.gifts[j].keep = true;
            }
          }
          item.gifts = item.gifts.filter((g) => g.keep);
        }
      }
    }
    await saveCart(cart);
    $nuxt.$emit("updateCart");
    $nuxt.$emit("informant:removeFromCart", { item });
    return;
  };
  const paypalApproved = (_) => {
    console.info("approved");
    console.warn(_);
  };
  const paypalCompleted = (_) => {
    console.info("completed");
    console.warn(_);
    if (_.state == "approved") {
      _.success = true;
    }
    $nuxt.$emit("sendOrder", { pay_id: _.id,force:true });
  };
  const defaultShipping = (cart) => {
    let defaultShipping = JSON.parse(
      JSON.stringify(
        store.getters.store.shippings.find((s) => s.type == "courier") ||
          store.getters.store.shippings[0]
      ) || "{}"
    );
    defaultShipping.address = {};
    return defaultShipping;
  };

  const skroutzOrder=(cart, order_id) =>{
    // let cart = JSON.parse(JSON.stringify(this.$store.getters.afterCart));
    skroutz_analytics("ecommerce", "addOrder", {
      order_id: cart.order_id, // Order ID. Required.
      revenue:
        cart.shipping.cost +
        cart.items.reduce(
          (acc, i) => acc + app.$hyperItem.actualPrice(i, false, i.quantity),
          0
        ), // Grand Total. Includes Tax and Shipping. Does not include payment costs.
      shipping: cart.shipping.cost, // Total Shipping Cost. Does not include payment costs.
      tax:
        cart.shipping.cost +
        cart.items.reduce(
          (acc, i) => acc + app.$hyperItem.actualPrice(i, false, i.quantity),
          0
        ) *
          0.24, // Total Tax.
      paid_by: cart.payment.type,
      paid_by_descr: cart.payment.title,
    });
    cart.items.forEach((i) => {
      let skroutz_id =
        (i.tags.filter((f) => f.key == "skroutz_id")[0] || {}).value ||
        i.sku ||
        i._id;
      skroutz_analytics("ecommerce", "addItem", {
        order_id: cart.order_id, // Order ID. Required.
        product_id: skroutz_id, // Product ID. Required.
        name: i.description, // Product Name. Required.
        price: app.$hyperItem.actualPrice(i, false), // Price per Unit. Required.
        quantity: i.quantity, // Quantity of Items. Required.
      });
    });
  }
  // Flush the latest localStorage cart to the server using sendBeacon so
  // that pending in-memory saves are not lost when the tab is killed
  // (mobile browser eviction, page close, bfcache).
  const flushCartSync = () => {
    try {
      if (typeof navigator === "undefined" || !navigator.sendBeacon) return;
      const raw = localStorage.getItem("cart");
      if (!raw) return;
      const cart = JSON.parse(raw);
      if (!cart || !(cart.items || []).length) return;
      const cart_id = $cookies.get("cart_id");
      if (!cart_id) return;
      const payload = JSON.stringify({
        cart,
        cart_id,
        store: { id: store.getters.store.id },
        user: (store.getters.user || {}).id,
      });
      const blob = new Blob([payload], { type: "application/json" });
      const base = ($axios.defaults.baseURL || "").replace(/\/$/, "");
      navigator.sendBeacon(`${base}/cart/sync`, blob);
    } catch (e) {
      // sendBeacon is best-effort; there's nothing to recover from here.
    }
  };
  if (process.browser) {
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushCartSync();
    });
    window.addEventListener("pagehide", flushCartSync);
  }

  // #15828 — Apple Pay / Google Pay (hosted MyPay checkout) never returns the
  // customer to /cart/success or /success: the MyPay checkout page (92887) only
  // configures the failure redirect (/cart/failed); on success the customer is
  // left on MyPay's receipt tab or comes back via the homepage link. So the
  // success-page cart clearing never runs and the just-purchased cart lingers.
  // Defensive guard, on every full page load: if the persisted cart was already
  // submitted as an order (sendOrder stamps cart.order_id) AND that order's
  // payment is confirmed (validation.success — set by the PSP webhook), reset
  // the cart. Failed/abandoned payments leave validation.success falsy, so
  // retry carts survive untouched. Self-disarms: the reset drops order_id.
  const clearPaidStaleCart = async () => {
    try {
      const cart = getCart();
      if (!cart || !cart.order_id || !(cart.items || []).length) {
        return;
      }
      const order = await $axios.$get(
        `orders/${cart.order_id}?store_id=${store.getters.store.store_id}`
      );
      if (!(((order || {}).validation || {}).success)) {
        return;
      }
      $cookies.remove("cart_id", {
        domain: `.${store.getters.store.eshop.url}`,
      });
      try {
        cookieStore.delete("cart_id");
      } catch (e) {}
      // Same empty-cart shape as the success pages' initCart.
      await saveCart(
        {
          id: null,
          skip: true,
          items: [],
          totalQ: 0,
          totalC: 0,
          donation: 0,
          sum: 0,
          step: 0,
          shipping: defaultShipping() || {
            cost: 0,
            address: {},
          },
          services: [],
          payment: {
            cost: 0,
          },
          shipping2: {
            address: "",
            town: "",
            postal_code: "",
            country: "",
            floor: "",
            phone: "",
            first_name: "",
            last_name: "",
          },
          billingAddress: false,
          parast: {
            type: "receipt",
          },
          comments: "",
          store_comments: "",
          invalid: {},
          user: {},
          checkout: "guest",
          messages: { giftcard: "" },
        },
        true
      );
      try {
        $nuxt.$emit("updateCart");
      } catch (e) {}
    } catch (e) {
      // Best-effort guard — never let it break boot.
    }
  };
  if (process.browser) {
    // Run shortly after boot so the app (and $nuxt) is mounted; the homepage
    // return from MyPay is a full page load, which is exactly when this fires.
    setTimeout(clearPaidStaleCart, 2500);
  }

  inject("hyperCart", {
    calculateCart,
    remoteCalculateCart,
    saveCart,
    getCart,
    fetchCart,
    addToCart,
    addFromBarcodes,
    removeItem,
    checkCartQuantity,
    increaseCartQuantity,
    decreaseCartQuantity,
    paypalApproved,
    paypalCompleted,
    defaultShipping,
    skroutzOrder,
    flushCartSync,
  });
};
